import { ChangeEvent, useEffect, useRef, useState } from "react";
import Button from "@app/components/button";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import { Images } from "@assets/images";
import clsx from "clsx";
import { Form, Formik } from "formik";
import AuthService from "@app/services/http/auth.service";
import { UpdateAuthor, UserInfo } from "@app/types";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { openPortalDialog } from "@app/services/modal.service";
import { MAX_SIZE_UPLOAD, SystemMessage } from "@app/constants";
import { addToast } from "@app/components/toast/toast.service";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import BankLinkModal from "@app/components/bank-link-modal";
import ChangePasswordModal from "@app/components/change-password-modal";
import ChangePasswordSuccessModal from "@app/components/change-password-success-modal/change-password-success-modal.component";
import OtpModal from "@app/components/otp-modal";

enum Tab {
  ACCOUNT = 1,
  SECURITY = 2,
}

function AccountInfo() {
  const [tab, setTab] = useState(Tab.ACCOUNT);
  const [userInfo, setUserInfo] = useState<UserInfo>();

  const { abortController } = useCancellationRequest();

  const [update, forceUpdate] = useForceUpdate();

  const inputThumbnailRef = useRef<HTMLInputElement>(null);

  const [errorMessage, setErrorMessage] = useState<{
    thumbnail: string;
  }>({
    thumbnail: "",
  });

  const changeTab = (tab: Tab) => {
    setTab(tab);
  };

  useEffect(() => {
    AuthService.getUserInfo(abortController.signal).then((res) => {
      setUserInfo(res.data);
    });
  }, [update]);

  const onSubmit = (values: UpdateAuthor) => {
    AuthService.changeInfo(values).then(() => {
      addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
      forceUpdate();
    });
  };

  const onChangeThumbnailFileClick = () => {
    if (inputThumbnailRef.current) {
      inputThumbnailRef.current.click();
    }
  };

  const handleThumbnailFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) {
      const newErrorMessage = {
        thumbnail: SystemMessage.INPUT_REQUIRED,
      };

      setErrorMessage(newErrorMessage);

      return;
    }

    const file = event.target.files[0];

    if (file.size > MAX_SIZE_UPLOAD) {
      const newErrorMessage = {
        thumbnail: SystemMessage.MAX_SIZE_UPLOAD,
      };

      setErrorMessage(newErrorMessage);

      return;
    }

    const newErrorMessage = {
      thumbnail: "",
    };

    setErrorMessage(newErrorMessage);

    AuthService.updateThumbnail(file).then(() => {
      const newErrorMessage = {
        thumbnail: "",
      };

      setErrorMessage(newErrorMessage);

      addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
      forceUpdate();
    });
  };

  const handleClickLinkBankAccount = () => {
    const bankModalObs = openPortalDialog(BankLinkModal, {
      isEdit: !!userInfo?.bankInfo,
      bank: userInfo?.bankInfo ? {
        ...userInfo.bankInfo,
        identityNumber: userInfo.bankInfo.identityNumber ?? undefined,
        issuedDate: userInfo.bankInfo.issuedDate ?? undefined,
        issuedPlace: userInfo.bankInfo.issuedPlace ?? undefined,
        beneficiaryAddress: userInfo.bankInfo.beneficiaryAddress ?? undefined,
      } : null,
    });

    bankModalObs.afterClosed().subscribe((data) => {
      if (data?.bank) {
        const action = userInfo?.bankInfo
          ? AuthService.updateBankAccount(data.bank)
          : AuthService.linkBankAccount(data.bank);

        action.then(() => {
          addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
          forceUpdate();
        });
      }
    });
  };

  const handleClickChangePassword = () => {
    const changePasswordModalObs = openPortalDialog(ChangePasswordModal);

    changePasswordModalObs.afterClosed().subscribe((data) => {
      if (data?.bodyPasswordChange) {
        AuthService.changePassword(data.bodyPasswordChange).then(() => {
          openPortalDialog(ChangePasswordSuccessModal);
        });
      }
    });
  };

  const handleRegisterMfa = () => {
    AuthService.sendMfaCode().then((res) => {
      if (res.data.secretImageUri) {
        const otpModelObs = openPortalDialog(OtpModal, {
          qrCodeUrl: res.data.secretImageUri,
        });

        otpModelObs.afterClosed().subscribe((data) => {
          if (data?.code) {
            AuthService.verifyMfaCode(data.code).then((res) => {
              if (!res.data.status) {
                addToast({
                  text: SystemMessage.VERIFY_OTP_FAILED,
                  status: "inValid",
                });
              } else {
                addToast({ text: SystemMessage.VERIFY_OTP_SUCCESSFULLY });
                forceUpdate();
              }
            });
          }
        });
      }
    });
  };

  return (
    <div className="py-6 px-12">
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <div className="flex">
          <div
            className={clsx(
              "w-[122px] h-[39px] text-[#ABAFB1] cursor-pointer rounded-t hover:bg-[#5570f1] hover:!text-white flex justify-center items-center border-b-4 border-solid border-transparent",
              {
                "!border-[#5570F1] !text-black": tab === Tab.ACCOUNT,
              }
            )}
            onClick={() => changeTab(Tab.ACCOUNT)}
          >
            Tài khoản
          </div>
          <div
            className={clsx(
              "w-[122px] h-[39px] text-[#ABAFB1] cursor-pointer rounded-t hover:bg-[#5570f1] hover:!text-white flex justify-center items-center border-b-4 border-solid border-transparent",
              {
                "!border-[#5570F1] !text-black": tab === Tab.SECURITY,
              }
            )}
            onClick={() => changeTab(Tab.SECURITY)}
          >
            Bảo mật
          </div>
        </div>
        <div className="mt-12">
          {tab === Tab.ACCOUNT && (
            <div>
              <div>
                <Formik
                  initialValues={{
                    fullName: userInfo?.detail.fullName ?? "",
                    email: userInfo?.detail.email ?? "",
                    phone: userInfo?.detail.phone ?? "",
                  }}
                  onSubmit={onSubmit}
                  validateOnBlur
                  validateOnChange
                  enableReinitialize
                >
                  <Form>
                    <div className="flex gap-32">
                      <div>
                        <div className="text-sm">Họ và tên</div>
                        <FormControl name="fullName">
                          <Input
                            className="mt-1 !w-[375px]"
                            inputClassName="!bg-[rgba(239,241,249,0.60)] !rounded-xl font-medium !h-[52px] !leading-[52px]"
                          />
                        </FormControl>
                        <div className="text-sm mt-4">Email</div>
                        <FormControl name="email">
                          <Input
                            className="mt-1 !w-[375px]"
                            inputClassName="!bg-[rgba(239,241,249,0.60)] !rounded-xl font-medium !h-[52px] !leading-[52px]"
                          />
                        </FormControl>
                        <div className="text-sm mt-4">Số điện thoại</div>
                        <FormControl name="phone">
                          <Input
                            className="mt-1 !w-[375px]"
                            inputClassName="!bg-[rgba(239,241,249,0.60)] !rounded-xl font-medium !h-[52px] !leading-[52px]"
                          />
                        </FormControl>
                        <div className="text-sm mt-4">Người theo dõi</div>
                        <Input
                          className="mt-1 !w-[375px]"
                          readOnly
                          value={String(userInfo?.detail?.followerCount ?? 0)}
                          inputClassName="!bg-[rgba(239,241,249,0.60)] !rounded-xl font-medium !h-[52px] !leading-[52px]"
                        />
                      </div>
                      <div>
                        <div className="relative">
                          <img
                            className="bg-red-400 rounded-full size-[132px]"
                            src={userInfo?.detail?.thumbnail}
                            alt=""
                          />
                          <input
                            ref={inputThumbnailRef}
                            type="file"
                            name="thumbnailFile"
                            hidden
                            onChange={handleThumbnailFileChange}
                          />
                          {errorMessage.thumbnail && (
                            <div className="flex error-color text-xs">
                              {errorMessage.thumbnail}
                            </div>
                          )}
                          <div
                            onClick={onChangeThumbnailFileClick}
                            className="absolute bottom-1 right-1"
                          >
                            <img
                              className="cursor-pointer"
                              src={Images.PenIcon.default}
                              alt=""
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      className="bg-[#00A653] rounded-[12px] text-white text-center w-60 h-10 leading-10 mt-5 cursor-pointer"
                      onClick={() => handleClickLinkBankAccount()}
                    >
                      Liên kết tài khoản ngân hàng
                    </div>
                    <Button
                      containerClassName="mt-[60px]"
                      width="auto"
                      type="submit"
                      className="!w-[118px] !h-[52px] font-medium !rounded-xl"
                      label="Cập nhật"
                    />
                  </Form>
                </Formik>
              </div>
            </div>
          )}
          {tab === Tab.SECURITY && (
            <div>
              <Formik
                initialValues={{
                  username: userInfo?.detail?.username ?? "",
                  newPassword: "",
                }}
                onSubmit={() => { }}
                validateOnBlur
                validateOnChange
                enableReinitialize
              >
                <Form>
                  {/* <div className="text-sm">Tên đăng nhập</div>
                  <FormControl name="username">
                    <Input
                      className="mt-1 !w-[375px]"
                      inputClassName="!bg-[rgba(239,241,249,0.60)] !rounded-xl font-medium !h-[52px] !leading-[52px]"
                    />
                  </FormControl>
                  <div className="text-sm mt-4">Mật khẩu mới</div>
                  <FormControl name="newPassword">
                    <Input
                      type="password"
                      className="mt-1 !w-[375px]"
                      inputClassName="!bg-[rgba(239,241,249,0.60)] !rounded-xl font-medium !h-[52px] !leading-[52px]"
                    />
                  </FormControl> */}
                  <div
                    className="!w-[317px] text-white p-3 !rounded-xl mt-2 cursor-pointer text-[14px] bg-[#4558FF] text-center"
                    onClick={handleClickChangePassword}
                  >
                    Đổi mật khẩu
                  </div>
                  {userInfo?.isMfaEnable === false && (
                    <Button
                      containerClassName="mt-[49px]"
                      width="auto"
                      className="!w-[317px] !h-[52px] font-medium !rounded-xl !bg-[#00A653] hover:!bg-[#00b45a]"
                      label="Enable two-factor authentication"
                      onClick={handleRegisterMfa}
                    />
                  )}
                  {/* <Button
                    containerClassName="mt-[60px]"
                    width="auto"
                    type="submit"
                    className="!w-[118px] !h-[52px] font-medium !rounded-xl"
                    label="Cập nhật"
                  /> */}
                </Form>
              </Formik>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccountInfo;
